import { productFamilyKey } from './DrawerEvidenceHelpers.jsx';

const MONTH_CODES = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const dateToMonthCode = (value) => {
  if (!value) return '';
  const raw = String(value).trim().toUpperCase();
  if (MONTH_CODES.includes(raw)) return raw;
  const iso = raw.match(/^(\d{4})-(\d{2})-\d{2}/);
  if (!iso) return raw;
  return MONTH_CODES[Number(iso[2]) - 1] || raw;
};

const rawValue = (row, key) => row?.rawRow?.[key] || row?.raw_row?.[key] || '';
const rawMetadataValue = (row, key) => row?.rawRow?.[key] ?? row?.raw_row?.[key];
const normalizedCode = (value) => String(value || '').trim().toUpperCase().replace(/_+$/, '');

const unresolvedCpqInheritance = (row = {}) => {
  const accountMatch = row.accountMatch || row.account_match;
  if (accountMatch?.matchStatus === 'matched' || accountMatch?.match_status === 'matched') return false;
  return Boolean(
    row.missingEvidenceLink
    || rawMetadataValue(row, 'missingEvidenceLink')
    || row.isQuarantined
    || rawMetadataValue(row, 'isQuarantined')
  );
};

const inheritedCpqProductCode = (row = {}, fallbackCode = '') => {
  const current = normalizedCode(fallbackCode);
  const candidate = normalizedCode(row.inheritedProductCode || row.inherited_product_code || row.accountMatch?.productCode || row.accountMatch?.evidence?.productCode);
  if (!current || !candidate || current === candidate) return '';
  const currentFamily = productFamilyKey(current);
  if (!currentFamily || currentFamily === 'UNCODED' || currentFamily !== productFamilyKey(candidate)) return '';
  return current === currentFamily ? candidate : '';
};

const cpqWebsiteTierName = (code) => {
  const value = String(code || '').trim().toUpperCase().replace(/\s+/g, '');
  if (!value) return '';
  const brandSuffix = '(?:_|$|C$|B$|G$|CAD$|K$|BG$|GB$|CBG$|BCG$|CGB$|BGC$|GCB$|GBC$|CG$|GC$|CB$|BC$)';
  if (new RegExp(`^DI_P1${brandSuffix}`).test(value)) return 'Plus';
  if (new RegExp(`^DI_P${brandSuffix}`).test(value)) return 'Premier';
  if (new RegExp(`^DI_B${brandSuffix}`).test(value)) return 'Base';
  return '';
};

const normalizeCpqProductName = (name, code) => {
  const tierName = cpqWebsiteTierName(code);
  if (!tierName) return name;
  const sourceName = String(name || '').trim();
  if (!sourceName || /website\s+package/i.test(sourceName)) return tierName;
  return sourceName;
};

export const normalizeCpqRowsForDrawer = (rows = []) =>
  rows.map((row) => {
    const budget = toNumber(row.budget);
    const quantity = Number.isFinite(Number(row.quantity)) ? Number(row.quantity) : 1;
    const isCredit = row.isCredit === true || row.is_credit === true || budget < 0 || quantity < 0;
    const inheritanceIsUnresolved = unresolvedCpqInheritance(row);
    const explicitBillable = row.isBillable ?? row.is_billable;
    const isBillable = inheritanceIsUnresolved && !isCredit
      ? budget > 0 && quantity > 0
      : explicitBillable ?? (!isCredit && budget > 0 && quantity > 0);
    const originalProductCode = row.normalizedProductCode || row.normalized_product_code || row.formulaProductCode || row.oemProductCode || '';
    const oemProductCode = (!inheritanceIsUnresolved && inheritedCpqProductCode(row, originalProductCode)) || originalProductCode;
    const productName = normalizeCpqProductName(row.productName || row.product_name || '', oemProductCode);
    return {
      ...row,
      dealerCode: row.bac,
      dealerName: row.dealerName || row.dealer_name || '',
      productName,
      oemProductCode,
      originalCpqProductCode: originalProductCode !== oemProductCode ? originalProductCode : row.originalCpqProductCode,
      budget,
      isBillable,
      isCredit,
      provider: toNumber(row.provider ?? row.Provider ?? rawValue(row, 'Provider')),
      providerCurrency: row.providerCurrency || row.provider_currency || rawValue(row, 'Provider Currency') || '',
      invoicePrice: toNumber(row.invoicePrice ?? row.invoice_price ?? row.provider ?? row.Provider ?? rawValue(row, 'Provider')),
      quantity,
      serviceMonth: row.serviceMonthRaw || row.service_month_raw || dateToMonthCode(row.serviceMonthResolved || row.service_month_resolved),
      invoiceNumber: row.invoiceNumber || row.invoice_number || '',
      orderItemId: row.orderItemId || row.order_item_id || undefined,
      matchedAppSheetOrderItemId: row.matchedAppSheetOrderItemId
        || row.matched_appsheet_order_item_id
        || row.accountMatch?.orderItemId
        || row.account_match?.orderItemId
        || row.account_match?.order_item_id
        || undefined,
      invoiceGroup: row.invoiceGroup || row.invoice_group || row.accountMatch?.evidence?.invoiceGroup || undefined,
      enrollmentDate: row.enrollmentDate || row.enrollment_date || '',
      activationDate: row.activationDate || row.activation_date || '',
      effectiveDate: row.effectiveDate || row.effective_date || '',
      productStatus: isCredit ? 'CANCELLED' : 'LIVE',
      missingEvidenceLink: inheritanceIsUnresolved ? true : row.missingEvidenceLink,
      inheritanceReason: row.inheritanceReason || rawMetadataValue(row, 'inheritanceReason') || row.quarantineReason || rawMetadataValue(row, 'quarantineReason') || '',
      isCurrentMonth: true,
    };
  });

export const normalizeGmRowsForDrawer = (rows = [], fallback = {}) =>
  rows.map((row) => {
    const match = row.accountMatch || row.account_match || null;
    return {
      ...row,
      bac: row.bac,
      dealerName: row.dealerName || row.dealer_name || rawValue(row, 'Dealer Name') || fallback.dealerName || '',
      vendorName: row.vendorName || row.vendor_name || rawValue(row, 'Vendor Name') || 'Dealer Inspire',
      vendorStatus: row.vendorStatus || row.vendor_status || '',
      isTerminated: row.isTerminated ?? row.is_terminated ?? false,
      isTerminatedDate: row.isTerminatedDate || row.terminatedDate || row.terminated_date || '',
      enrolledDate: row.enrolledDate || row.enrolled_date || '',
      enrollmentStatus: row.enrollmentStatus || row.enrollmentStatusCode || row.enrollment_status_code || '',
      optOutDate: row.optOutDate || row.opt_out_date || '',
      lastUpdatedDate: row.lastUpdatedDate || row.last_updated_date || '',
      productCode: row.productCode || row.product_code || '',
      productSelection: row.productSelection || row.product_selection || row.productName || '',
      productDesignation: row.productDesignation || row.product_designation || '',
      productBrand: row.productBrand || row.product_brand || '',
      productStatus: row.productStatus || row.product_status || (row.isBilling || row.is_billing ? 'LIVE' : 'CANCELLED'),
      isBilling: Boolean(row.isBilling ?? row.is_billing),
      dealerCost: toNumber(row.dealerCost ?? row.dealer_cost),
      productSelectionDate: row.productSelectionDate || row.product_selection_date || '',
      goLiveDate: row.goLiveDate || row.go_live_date || '',
      cancelDate: row.cancelDate || row.cancel_date || '',
      billingStartDate: row.billingStartDate || row.billing_start_date || '',
      billingEndDate: row.billingEndDate || row.billing_end_date || '',
      effectiveDate: row.effectiveDate || row.effective_date || '',
      notes: row.notes || rawValue(row, 'Notes') || '',
      accountMatch: match,
    };
  });
